
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { UseFormReturn } from 'react-hook-form';

interface SalaryFieldProps {
  id: string;
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  form?: UseFormReturn<any>;
  name?: string;
}

const SalaryField: React.FC<SalaryFieldProps> = ({ 
  id, 
  label, 
  value, 
  onChange,
  form,
  name = "salary"
}) => {
  // Handle formatting salary for display and parsing
  const formatSalaryForDisplay = (value: number | undefined) => {
    if (!value) return '';
    return value.toString();
  };

  const parseSalaryInput = (input: string) => {
    // Remove non-numeric characters
    const numericValue = input.replace(/[^0-9.]/g, '');
    
    if (numericValue === '') {
      return undefined;
    }
    
    const parsedValue = parseFloat(numericValue);
    return isNaN(parsedValue) ? undefined : parsedValue;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseSalaryInput(e.target.value);
    onChange(parsedValue);
  };

  // For standard component usage
  if (!form) {
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <Input
            id={id}
            type="text"
            inputMode="numeric"
            className="pl-6"
            value={formatSalaryForDisplay(value)}
            onChange={handleInputChange}
            placeholder="e.g. 75000"
          />
        </div>
        <p className="text-xs text-redflags-gray">Enter annual salary (the + Super will be added automatically)</p>
      </div>
    );
  }

  // For react-hook-form integration
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel htmlFor={id}>{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500">$</span>
              </div>
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                className="pl-6"
                value={formatSalaryForDisplay(field.value)}
                onChange={(e) => {
                  const parsedValue = parseSalaryInput(e.target.value);
                  field.onChange(parsedValue);
                }}
                placeholder="e.g. 75000"
              />
            </div>
          </FormControl>
          <p className="text-xs text-redflags-gray">Enter annual salary (the + Super will be added automatically)</p>
        </FormItem>
      )}
    />
  );
};

export default SalaryField;
